/**
 * API Helper Utilities
 * Extracted from App.js as part of Phase 1 refactoring.
 * 
 * These are utility functions for API error handling and file naming.
 * Used throughout the application for consistent API interactions.
 */

/**
 * Extract a user-friendly error message from an API error response.
 * Handles various error formats from FastAPI/axios.
 * @param {Error|Object} error - The error object from axios catch
 * @param {string} defaultMessage - Fallback message if no detail found
 * @returns {string} Human-readable error message
 */
export const getApiErrorMessage = (error, defaultMessage = "An error occurred") => {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || defaultMessage;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return defaultMessage;
};

/**
 * Generate consistent download filenames for client documents.
 * Format: "LastName, FirstName - Document Type.extension"
 * @param {Object} client - Client object with full_name property
 * @param {string} documentType - Type of document being downloaded
 * @param {string} extension - File extension (default: 'pdf')
 * @returns {string} Formatted filename
 */
export const generateDownloadFilename = (client, documentType, extension = 'pdf') => {
  const nameParts = (client?.full_name || 'Unknown').trim().split(' ');
  const firstName = nameParts.slice(0, -1).join(' ') || nameParts[0] || 'Unknown';
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  const formattedName = lastName ? `${lastName}, ${firstName}` : firstName;
  
  // Clean up document type and filename
  const cleanDocType = documentType.replace(/[<>:"/\\|?*]/g, '');
  const filename = `${formattedName} - ${cleanDocType}.${extension}`;
  
  return filename;
};

/**
 * Extract the server-provided filename from a response's Content-Disposition
 * header (RFC 5987 filename* preferred). Returns fallback when absent.
 */
export const filenameFromResponse = (response, fallback) => {
  const cd = response?.headers?.['content-disposition'] || '';
  const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch (e) { /* fall through */ }
  }
  const plain = cd.match(/filename="([^"]+)"/i);
  if (plain) return plain[1];
  return fallback;
};
